import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { polygonCentroid, polygonAreaM2 } from "@/components/map/LeafletMap";
import { Loader2, Layers, CreditCard, CheckCircle2, MapPin, Route as RouteIcon, Target, TrendingUp, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SharePreviewDialog from "@/components/share/SharePreviewDialog";
import ProspectBoard from "@/components/shop/ProspectBoard";
import { fmtMoney, scoreColor } from "@/lib/prospects";
import { toast } from "sonner";

const km2 = (poly) => (polygonAreaM2(poly || []) / 1e6);

function SummaryStat({ icon: Icon, label, value, className = "" }) {
  return (
    <div className="rounded-xl border border-border bg-background/40 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />{label}
      </div>
      <div className={`mt-1 text-2xl font-heading font-extrabold ${className}`}>{value}</div>
    </div>
  );
}

export default function MyZonesPanel() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [syncing, setSyncing] = useState(true);
  const [osmStatus, setOsmStatus] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null); // null = All Zones
  const [pendingLeases, setPendingLeases] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/my/zones"); setZones(data); }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
    (async () => {
      try { const { data } = await api.get("/shop/pending-leases"); setPendingLeases(data || []); }
      catch (e) { /* non-blocking */ }
    })();
    (async () => {
      try {
        const { data } = await api.get("/my/prospects");
        setProspects(data.prospects || []);
        setOsmStatus(data.osm_status);
      } catch (e) { setOsmStatus("unavailable"); }
      finally { setSyncing(false); }
    })();
  }, []);

  const onUpdate = async (id, payload) => {
    const { data } = await api.patch(`/my/prospects/${id}`, payload);
    setProspects((arr) => arr.map((p) => (p.id === data.id ? data : p)));
  };
  const onSaveToRider = async (p, photos) => {
    const { data } = await api.post(`/my/prospects/${p.id}/save-to-rider`, { photos: photos || [] });
    setProspects((arr) => arr.map((x) => (x.id === data.id ? data : x)));
    return data;
  };
  const onRemoveFromRider = async (p) => {
    const { data } = await api.delete(`/my/prospects/${p.id}/rider`);
    setProspects((arr) => arr.map((x) => (x.id === data.id ? data : x)));
    return data;
  };

  // Group zones by bike path for a scalable, organized layout.
  const groups = useMemo(() => {
    const m = new Map();
    for (const z of zones) {
      const key = z.bike_path_id || "_";
      if (!m.has(key)) m.set(key, { id: key, name: z.bike_path_name || "Unassigned bike path", zones: [] });
      m.get(key).zones.push(z);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [zones]);

  const selectedZone = useMemo(() => zones.find((z) => z.id === selectedZoneId) || null, [zones, selectedZoneId]);

  // Prospects + polygons for the current view (all zones vs one zone).
  const viewProspects = useMemo(
    () => (selectedZone ? prospects.filter((p) => p.zone_id === selectedZone.id) : prospects),
    [prospects, selectedZone]
  );
  const viewPolygons = useMemo(
    () => (selectedZone ? [selectedZone.polygon || []] : zones.map((z) => z.polygon || [])).filter((p) => p.length >= 3),
    [zones, selectedZone]
  );
  // Headline metrics come from /my/zones (fast, authoritative per-zone values) so
  // the summary and ProspectBoard stay correct even while /my/prospects refreshes
  // OSM data in the background. Map pins still use the live prospect list.
  const zoneOpp = (z) => ({ prospectCount: z.prospectCount ?? 0, opportunityScore: z.opportunityScore ?? 0, projectedMonthly: z.projectedMonthly ?? 0, projectedAnnual: z.projectedAnnual ?? 0 });
  const viewCenter = selectedZone ? (polygonCentroid(selectedZone.polygon) || [41.75, -71.3]) : (polygonCentroid(zones[0]?.polygon) || [41.75, -71.3]);
  const viewBoundary = selectedZone ? (selectedZone.bike_path_boundary || []) : [];

  // Aggregate metrics across every owned zone (summed from server per-zone values).
  const agg = useMemo(() => {
    const prospectsN = zones.reduce((a, z) => a + (z.prospectCount || 0), 0);
    const monthly = zones.reduce((a, z) => a + (z.projectedMonthly || 0), 0);
    const annual = zones.reduce((a, z) => a + (z.projectedAnnual || 0), 0);
    const wScore = zones.reduce((a, z) => a + (z.opportunityScore || 0) * (z.prospectCount || 0), 0);
    const score = prospectsN ? Math.round(wScore / prospectsN) : 0;
    const area = zones.reduce((a, z) => a + km2(z.polygon), 0);
    const paths = new Set(zones.map((z) => z.bike_path_id)).size;
    return { zones: zones.length, paths, area, prospects: prospectsN, score, annual, monthly };
  }, [zones]);

  const viewOpportunity = selectedZone
    ? zoneOpp(selectedZone)
    : { prospectCount: agg.prospects, opportunityScore: agg.score, projectedMonthly: agg.monthly, projectedAnnual: agg.annual };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (!zones.length) return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="myzones-empty">
      <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
      <div className="font-heading font-bold">No service area yet</div>
      <p className="text-sm text-muted-foreground mt-1">Velopaths will assign your zone(s) inside a bike path. Once assigned, your territory prospects appear here.</p>
    </div>
  );

  const pending = pendingLeases;

  return (
    <div className="space-y-5" data-testid="myzones-panel">
      {/* Aggregate territory summary */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="myzones-aggregate-summary">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-heading text-lg font-bold">My Service Territories</h2>
            <p className="text-sm text-muted-foreground">{agg.zones} zone{agg.zones !== 1 ? "s" : ""} across {agg.paths} bike path{agg.paths !== 1 ? "s" : ""}. Select a territory below to focus its prospects and map, or stay on All Zones for the combined view.</p>
          </div>
          {syncing && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" />Syncing prospects…</span>}
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          <SummaryStat icon={MapPin} label="Zones" value={agg.zones} className="text-primary" />
          <SummaryStat icon={RouteIcon} label="Bike paths" value={agg.paths} className="text-primary" />
          <SummaryStat icon={Globe} label="Territory" value={`${agg.area.toFixed(2)} km²`} />
          <SummaryStat icon={Target} label="Prospects" value={agg.prospects} />
          <SummaryStat icon={CheckCircle2} label="Opportunity" value={`${agg.score}/100`} className={scoreColor(agg.score)} />
          <SummaryStat icon={TrendingUp} label="Est. annual rev" value={fmtMoney(agg.annual)} className="text-emerald-400" />
        </div>
      </div>

      {pending.length > 0 && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-2" data-testid="myzones-approved-banner">
          <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /><h3 className="font-heading font-bold text-sm">Approved — complete payment to activate</h3></div>
          <p className="text-xs text-muted-foreground">These zones are reserved for you. Pay the annual fee to activate and start managing advertisers, routes, stops and content. Reserved zones expire if unpaid.</p>
          {pending.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 rounded-lg bg-background/50 border border-border p-3" data-testid={`myzones-pay-row-${l.id}`}>
              <div className="text-sm">
                <span className="font-medium">{l.zone_name}</span> <span className="text-muted-foreground">· ${Number(l.fee || 0).toFixed(0)}/yr</span>
                {l.reservation_expires_at && <span className="block text-[11px] text-amber-400">Reserved until {new Date(l.reservation_expires_at).toLocaleDateString()}</span>}
              </div>
              <Button size="sm" disabled={!l.pay_link} onClick={() => { if (l.pay_link) window.location.href = l.pay_link; }} data-testid={`myzones-pay-${l.id}`}><CreditCard className="w-4 h-4 mr-1.5" />Pay &amp; activate</Button>
            </div>
          ))}
        </div>
      )}

      {/* Territory selector: All Zones + zone cards grouped by bike path */}
      <div className="space-y-4" data-testid="myzones-selector">
        <button
          type="button"
          onClick={() => setSelectedZoneId(null)}
          data-testid="myzone-all-view"
          className={`w-full flex items-center justify-between gap-3 rounded-xl border p-4 text-left transition-colors ${selectedZoneId === null ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-card hover:bg-secondary/40"}`}
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary"><Layers className="w-5 h-5" /></span>
            <div>
              <div className="font-heading font-bold text-sm">All Zones</div>
              <div className="text-xs text-muted-foreground">Combined metrics and every territory on the map</div>
            </div>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div><span className="font-bold text-foreground">{agg.prospects}</span> prospects</div>
            <div className="text-emerald-400">{fmtMoney(agg.monthly)}/mo</div>
          </div>
        </button>

        {groups.map((g) => (
          <div key={g.id} data-testid={`myzone-group-${g.id}`}>
            <div className="flex items-center gap-2 mb-2 px-0.5">
              <RouteIcon className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-heading font-bold text-sm">{g.name}</h3>
              <Badge variant="outline" className="text-[10px]">{g.zones.length} zone{g.zones.length !== 1 ? "s" : ""}</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.zones.map((z) => {
                const active = z.id === selectedZoneId;
                return (
                  <div
                    key={z.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedZoneId(z.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedZoneId(z.id); } }}
                    data-testid={`myzone-card-${z.id}`}
                    className={`cursor-pointer rounded-xl border p-4 transition-all ${active ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-card hover:border-primary/40 hover:bg-secondary/30"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-heading font-bold text-sm flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-primary shrink-0" />{z.name}
                      </div>
                      {z.needs_payment && <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[10px] shrink-0">Pending payment</Badge>}
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                      <div><div className="text-sm font-bold">{z.prospectCount ?? 0}</div><div className="text-[10px] text-muted-foreground">Prospects</div></div>
                      <div><div className={`text-sm font-bold ${scoreColor(z.opportunityScore ?? 0)}`}>{z.opportunityScore ?? 0}</div><div className="text-[10px] text-muted-foreground">Opp /100</div></div>
                      <div><div className="text-sm font-bold">{km2(z.polygon).toFixed(2)}</div><div className="text-[10px] text-muted-foreground">km²</div></div>
                    </div>
                    <div className="mt-3 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[11px] text-emerald-400">{fmtMoney(z.projectedMonthly)}/mo est.</span>
                      <SharePreviewDialog title={z.name} triggerLabel="Share" triggerTestId={`myzone-share-${z.id}`} url={`${window.location.origin}/preview/bike-path/${z.bike_path_id}?zone=${z.id}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Territory metrics + map for the current view */}
      <ProspectBoard
        key={selectedZoneId || "all"}
        title={selectedZone ? `${selectedZone.name} — ${selectedZone.bike_path_name}` : "All Service Territories"}
        prospects={viewProspects}
        opportunity={viewOpportunity}
        osmStatus={osmStatus}
        syncing={syncing}
        zonePolygons={viewPolygons}
        boundary={viewBoundary}
        center={viewCenter}
        onUpdate={onUpdate}
        onSaveToRider={onSaveToRider}
        onRemoveFromRider={onRemoveFromRider}
      />
    </div>
  );
}
